/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";
import { useMemo, useRef, useState } from "react";
import moment from "moment";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";

import {
  Avatar,
  Card,
  Chip,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  CakeRounded,
  CalendarMonthRounded,
  Celebration,
  ChevronLeft,
  ChevronRight,
  CelebrationRounded,
  LensRounded,
  FestivalRounded,
  TempleBuddhistRounded,
	Cake,
} from "@mui/icons-material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";
import CalendarRoot from "examples/Calendar/CalendarRoot";
import { useMaterialUIController } from "context";

const EVENT_META = {
  birthday: {
    label: "Birthday",
    icon: CakeRounded,
    accent: colors.info.main,
    soft: "#F3ECFF",
  },
  anniversary: {
    label: "Anniversary",
    icon: CelebrationRounded,
    accent: "#FF9E45",
    soft: "#FFF4E7",
  },
  holiday: {
    label: "Holiday",
    icon: FestivalRounded,
    accent: "#56C587",
    soft: "#EDFFF4",
  },
};

function Calendar({ header = { title: "", date: "" }, employees = [], holidays = [], ...rest }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const isMobile = useMediaQuery("(max-width:900px)");
  const calendarRef = useRef(null);
  const [calendarRangeLabel, setCalendarRangeLabel] = useState("");

  const getDateWithoutYear = (dateString) => {
    const dateObj = new Date(dateString);
    if (!isNaN(dateObj)) {
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      return `${new Date().getFullYear()}-${month}-${day}`;
    }
    return dateString;
  };

  const employeeEvents = useMemo(
    () =>
      employees
        .filter((employee) => employee.status === "active")
        .flatMap((employee) => [
          {
            title: `${employee.user.firstname} ${employee.user.lastname}`,
            date: getDateWithoutYear(employee.dob),
            imageUrl: employee.img,
            firstName: employee.user.firstname,
            lastName: employee.user.lastname,
            milestoneType: "birthday",
          },
          {
            title: `${employee.user.firstname}
						 ${employee.user.lastname}`,
            date: getDateWithoutYear(employee.doj),
            imageUrl: employee.img,
            firstName: employee.user.firstname,
            lastName: employee.user.lastname,
            milestoneType: "anniversary",
            yearsLabel: employee.doj ? `${moment().diff(moment(employee.doj), "years")} Years` : "",
          },
        ]),
    [employees]
  );

  const holidayEvents = useMemo(
    () =>
      holidays.map((holiday) => ({
        ...holiday,
        milestoneType: "holiday",
      })),
    [holidays]
  );

  const calendarEvents = useMemo(
    () =>
      [...employeeEvents, ...holidayEvents].map((event) => ({
        ...event,
        className: `event-${event.milestoneType || "birthday"}`,
      })),
    [employeeEvents, holidayEvents]
  );

  const handleCalendarNav = (action) => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;
    calendarApi[action]();
  };

  const handleGoToToday = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;
    calendarApi.today();
  };

  const renderEventContent = (eventInfo) => {
    const { milestoneType, firstName, imageUrl, yearsLabel } = eventInfo.event.extendedProps;
    const eventMeta = EVENT_META[milestoneType] || EVENT_META.birthday;
    const EventIcon = eventMeta.icon;
    const title = eventInfo.event.title;
    const initials = (firstName || title || "E").charAt(0).toUpperCase();

    if (milestoneType === "holiday") {
      return (
        <MDBox className="weekly-event-card weekly-event-card--holiday">
          <MDBox className="weekly-event-hero weekly-event-hero--holiday">
            <TempleBuddhistRounded fontSize="small" />
          </MDBox>
          <Typography className="weekly-event-title">{title}</Typography>
          <Chip
            size="small"
            icon={<FestivalRounded />}
            label="Holiday"
            className="weekly-event-chip weekly-event-chip--holiday"
          />
        </MDBox>
      );
    }

    return (
      <MDBox className="weekly-event-card">
        <Avatar src={imageUrl} alt={title} className={`weekly-event-avatar weekly-event-avatar--${milestoneType}`}>
          {initials}
        </Avatar>
        <Typography className="weekly-event-title">{title}</Typography>
        <Chip
          size="small"
          icon={<EventIcon />}
          label={eventMeta.label}
          className={`weekly-event-chip weekly-event-chip--${milestoneType}`}
        />
        {/*{milestoneType === "anniversary" && yearsLabel ? (
          <Typography className="weekly-event-subtitle">{yearsLabel}</Typography>
        ) : null}*/}
      </MDBox>
    );
  };

  const legendItems = [
    { key: "birthday", label: "Birthday" },
    { key: "anniversary", label: "Anniversary" },
    { key: "holiday", label: "Holiday" },
  ];

  return (
    <Card
      sx={{
        p: { xs: 1.5, md: 0 },
        borderRadius: "30px",
        overflow: "hidden",
        border: `1px solid ${rgba(colors.secondary.main, 0.12)}`,
        boxShadow: `0 24px 50px ${rgba(colors.dark.main, 0.06)}`,
      }}
    >
      <MDBox
        sx={{
          px: { xs: 0.5, md: 3 },
          py: { xs: 1, md: 2.5 },
          borderBottom: `1px solid ${rgba(colors.secondary.main, 0.08)}`,
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: { xs: "flex-start", lg: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <MDBox
            sx={{
              width: 48,
              height: 48,
              borderRadius: "16px",
              display: "grid",
              placeItems: "center",
              background: `linear-gradient(180deg, ${rgba(colors.info.main, 0.14)} 0%, ${rgba(
                colors.info.main,
                0.08
              )} 100%)`,
              color: colors.info.main,
            }}
          >
            <CalendarMonthRounded sx={{ fontSize: 24 }} />
          </MDBox>
          <MDBox>
            <MDTypography
              variant="h5"
              sx={{ color: colors.dark.main, lineHeight: 1.05 }}
            >
              Weekly Calendar
            </MDTypography>
            <MDTypography variant="body2" sx={{ mt: 0.35, color: colors.text.main, fontSize: "0.95rem" }}>
              Birthdays, Anniversaries & Holidays
            </MDTypography>
          </MDBox>
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.25}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <Chip
            label="Week View"
            sx={{
              height: 40,
              borderRadius: "14px",
              background: `linear-gradient(135deg, ${colors.info.main} 0%, ${colors.primary.main} 100%)`,
              color: colors.white.main,
              fontSize: "0.9rem",
              fontWeight: 700,
              "& .MuiChip-label": {
                px: 2.25,
              },
            }}
          />

          <Stack direction="row" spacing={1}>
            <Chip
              label="Today"
              onClick={handleGoToToday}
              sx={{
                height: 40,
                borderRadius: "14px",
                backgroundColor: colors.white.main,
                border: `1px solid ${rgba(colors.secondary.main, 0.16)}`,
                color: colors.dark.main,
                fontSize: "0.9rem",
                fontWeight: 700,
                "& .MuiChip-label": { px: 2.1 },
              }}
            />
            <IconButton
              onClick={() => handleCalendarNav("prev")}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "14px",
                border: `1px solid ${rgba(colors.secondary.main, 0.14)}`,
              }}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              onClick={() => handleCalendarNav("next")}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "14px",
                border: `1px solid ${rgba(colors.secondary.main, 0.14)}`,
              }}
            >
              <ChevronRight />
            </IconButton>
          </Stack>
        </Stack>
      </MDBox>

      <MDBox
        sx={{
          px: { xs: 0.5, md: 3 },
          py: { xs: 1.5, md: 2.5 },
          display: "flex",
          flexDirection: { xs: "column", xl: "row" },
          alignItems: { xs: "flex-start", xl: "center" },
          justifyContent: "space-between",
          gap: 2,
          borderBottom: `1px solid ${rgba(colors.secondary.main, 0.08)}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap">
          <Celebration sx={{ fontSize: 24, color: colors.info.main }} />
          <MDTypography
            variant="h5"
            sx={{
              color: colors.info.main,
              fontWeight: 700,
              letterSpacing: "-0.02em",
            }}
          >
            {calendarRangeLabel || header.date}
          </MDTypography>
          {!isMobile ? <Cake sx={{ fontSize: 16, color: colors.warning.main }} /> : null}
        </Stack>

        <Stack direction="row" spacing={1.1} flexWrap="wrap" useFlexGap>
          {legendItems.map((item) => (
            <Chip
              key={item.key}
              label={item.label}
              sx={{
                height: 36,
                borderRadius: "999px",
                backgroundColor: colors.white.main,
                border: `1px solid ${rgba(colors.secondary.main, 0.14)}`,
                color: colors.dark.main,
                fontWeight: 600,
                fontSize: "0.88rem",
                "&::before": {
                  content: '""',
                  display: "inline-block",
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  marginRight: "8px",
                  backgroundColor: EVENT_META[item.key].accent,
                },
              }}
            />
          ))}
        </Stack>
      </MDBox>

      <CalendarRoot p={{ xs: 0.5, md: 2 }} ownerState={{ darkMode }}>
        <FullCalendar
          ref={calendarRef}
          {...rest}
          initialView="dayGridWeek"
          plugins={[dayGridPlugin, interactionPlugin]}
          events={calendarEvents}
          eventContent={renderEventContent}
          eventDisplay="block"
          dayMaxEventRows={false}
          dayMaxEvents={false}
          fixedWeekCount={false}
          height="auto"
          headerToolbar={false}
          datesSet={(dateInfo) => {
            const start = moment(dateInfo.start);
            const end = moment(dateInfo.end).subtract(1, "day");
            setCalendarRangeLabel(`${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`);
          }}
          dayCellClassNames={(arg) => {
            const day = moment(arg.date).day();
            if (day === 0 || day === 6) {
              const hasEvent = arg.view.calendar.getEvents().some(
                (event) =>
                  moment(event.start).format("YYYY-MM-DD") === moment(arg.date).format("YYYY-MM-DD")
              );
              return hasEvent ? ["fc-weekend-cell", "fc-weekend-has-event"] : ["fc-weekend-cell"];
            }
            return [];
          }}
          views={{
            dayGridWeek: {
              dayHeaderFormat: { weekday: "short", month: "short", day: "numeric" },
            },
          }}
        />
      </CalendarRoot>
    </Card>
  );
}

Calendar.propTypes = {
  header: PropTypes.shape({
    title: PropTypes.string,
    date: PropTypes.string,
  }),
  employees: PropTypes.array,
  holidays: PropTypes.array,
};

export default Calendar;
